<script>
import useSharedData from "@/use/SharedData.js";
import useValidation from "@/use/Validation.js";
import useTools from "@/use/Tools.js";
import {computed, onMounted, ref} from "vue";
import useSharedMethod from "@/use/SharedMethod.js";
import {useTemplateStore} from "@/stores/Edms/Template.js";
import useSharedSelectData from "@/use/SharedSelectData";
import StrTemplateAutocomplete from "@/views/Edms/Template/Components/StrTemplateAutocomplete.vue";
import StrHRVariables from "@/components/StrHRVariables.vue";
import axios from "axios";

export default {
    components: {StrHRVariables, StrTemplateAutocomplete},
    props: {
        addAction: Boolean,
        updateAction: Boolean,
        deleteAction: Boolean,
        viewAction: Boolean,
        archiveAction: Boolean,
        unarchiveAction: Boolean,
        type: String,
    },

    setup(props) {
        const {mxRules} = useValidation();
        const {loading, dialog, overlay, inputColor, backgroundColor} = useSharedData();
        const {$route, $refs} = useTools();
        const {getBack} = useSharedMethod();
        const {documentModules} = useSharedSelectData();

        const templateStore = useTemplateStore();

        const readonly = computed(() => props.viewAction || props.deleteAction);

        const template = ref({
            module: props.module,
        });
        const html = ref("");

        function findById(id) {
            loading.value = true;
            axios.get(`/templates/${id}`).then((response) => {
                template.value = response.data;
                html.value = template.value.content;
            }).finally(() => {
                loading.value = false;
            });
        }

        function save() {
            if ($refs.form.validate()) {
                loading.value = true;
                template.value.content = html.value;
                template.value.type = props.type;
                axios.post('/templates', template.value).then((response) => {
                    templateStore.addTemplate(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        function update() {
            if ($refs.form.validate()) {
                loading.value = true;
                template.value.content = html.value;
                template.value.type = props.type;
                axios.put('/templates', template.value).then((response) => {
                    if (props.archiveAction || props.unarchiveAction) {
                        templateStore.deleteTemplate(response.data);
                    } else {
                        templateStore.updateTemplate(response.data);
                    }
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        function remove() {
            loading.value = true;
            template.value.content = html.value;
            axios.delete(`/templates/${template.value.id}`).then(() => {
                templateStore.deleteTemplate(template.value);
                getBack();
            }).finally(() => {
                loading.value = false;
            });
        }

        function archive() {
            template.value.archive = true;
            update();
        }

        function unarchive() {
            template.value.archive = false;
            update();
        }

        function addVariable(variable) {
            $refs.StrQuillEditor.addVariable(variable);
        }

        onMounted(() => {
            if (!props.addAction) {
                findById($route.params.id);
            }
        });

        return {
            template,
            html,
            inputColor,
            backgroundColor,
            mxRules,
            readonly,
            loading,
            dialog,
            overlay,
            documentModules,
            save,
            update,
            remove,
            archive,
            unarchive,
            addVariable,
            getBack,
        };
    },
};
</script>

<template>
    <v-dialog
        v-model="dialog"
        persistent
        scrollable
        max-width="900"
    >
        <v-card>
            <w-dialog-toolbar
                :add-action="addAction"
                :update-action="updateAction"
                :delete-action="deleteAction"
                :view-action="viewAction"
                :archive-action="archiveAction"
                :unarchive-action="unarchiveAction"
            ></w-dialog-toolbar>
            <w-overlay
                :overlay="overlay"
                :loading="loading"
                :delete-action="deleteAction"
                :archive-action="archiveAction"
                :unarchive-action="unarchiveAction"
                @remove="remove"
                @archive="archive"
                @unarchive="unarchiveAction"
                @cancel="overlay = false"
            />
            <v-card-text style="background-color: #FAFAFA;">
                <v-form lazy-validation ref="form">
                    <v-container fluid>
                        <w-fieldset>
                            <v-row dense>
                                <v-col cols="6">
                                    <w-label :label="$t('common.label')" required/>
                                    <w-text-field
                                        v-model="template.name"
                                        auto-focus
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="6">
                                    <w-label :label="$t('common.module')" required/>
                                    <w-enum-select
                                        v-model="template.module"
                                        :items="documentModules"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="6" v-if="template.module && type === 'CONTENT'">
                                    <w-label :label="$t('module.header_template')"/>
                                    <str-template-autocomplete
                                        v-model="template.headerId"
                                        type="HEADER"
                                        :module="template.module"
                                        :readonly="readonly"
                                    />
                                </v-col>
                                <v-col cols="6" v-if="template.module && type === 'CONTENT'">
                                    <w-label :label="$t('module.footer_template')"/>
                                    <str-template-autocomplete
                                        v-model="template.footerId"
                                        type="FOOTER"
                                        :module="template.module"
                                        :readonly="readonly"
                                    />
                                </v-col>
                            </v-row>
                        </w-fieldset>
                        <br/>
                        <w-fieldset>
                            <v-row dense>
                                <v-col cols="12" md="12">
                                    <str-quill-editor
                                        ref="StrQuillEditor"
                                        v-model="html"
                                        :disabled="readonly"
                                    />
                                </v-col>
                            </v-row>
                        </w-fieldset>
                    </v-container>
                </v-form>
            </v-card-text>
            <v-divider/>
            <v-card-actions>
                <StrHRVariables
                    :module="template.module"
                    @addVariable="addVariable"
                />
                <v-spacer/>
                <w-btn-close @press="getBack"/>
                <w-btn-add
                    :show="addAction"
                    :loading="loading"
                    @press="save(false)"
                />
                <w-btn-update
                    :show="updateAction"
                    :loading="loading"
                    @press="update"
                />
                <w-btn-delete
                    :show="deleteAction"
                    :loading="loading"
                    @press="overlay = true"
                />
                <w-btn-archive
                    :show="archiveAction"
                    :loading="loading"
                    @press="overlay = true"
                />
                <w-btn-unarchive
                    :show="unarchiveAction"
                    :loading="loading"
                    @press="overlay = true"
                />
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>